/**
 * WOHIN DIESE AUFRUFE GEHEN, HAT SICH GETEILT.
 *
 * Die Skript-Bibliothek ist seit dem 2026-08-20 eine eigenstaendige Sache und antwortet unter
 * `/api/skript-bibliothek`. Alles, was Anruf-Daten, Leads oder die KI anfasst, bleibt unter
 * `/api/sales-crm` — namentlich die beiden Bestenlisten, `evolve`, `seed`, `evolution-suggest`
 * und `personalize`.
 *
 * Die alte Adresse traegt die Bibliotheks-Pfade weiterhin, als Weiterleitung fuer einen Browser
 * mit gecachtem JavaScript (`api/main.py`, zweimal `include_router`). Sie faellt mit dem Folge-PR
 * weg; ab dann antwortet nur noch die neue.
 *
 * ⚠️ NOCH OFFEN, bewusst nicht in diesem PR: die Bibliotheks-Methoden hier stehen weiterhin in
 * `salesCrmApi` statt in einer eigenen Datei. Sie liegen nicht am Stueck, sondern zwischen den
 * Sales-CRM-Methoden verteilt, und sie herauszuziehen hiesse, jede Aufrufstelle in `SalesCrm.tsx`,
 * `CallCockpit.tsx` und `salesCrmSlice.ts` mit anzufassen. Das gehoert in denselben Schritt, der
 * die Weiterleitungen entfernt, statt zweimal durch dieselben Dateien zu gehen.
 */
import { api } from './api';
// Der Kanal. Dieselbe Bibliothek trägt das Telefon-Skript, die Mail und LinkedIn; getrennt
// sind die Bestände über `bereich` (siehe docs/umbau-bereich-spalte.md). Ohne Angabe meint
// jeder Aufruf das Telefon-Skript — so wie vor dem Umbau.
import type { FassungsBereich } from '../types';
import type {
  Lead,
  SaleCall,
  ScriptVersion,
  SalesKpis,
  ScriptVariant,
  VariantLeaderboardEntry,
  MyForkResponse,
  EffectiveScript,
  LibraryScript,
  BlockLeaderboard,
  CallScript,
  SectionTags,
  EvolutionSuggestion,
  ObjectionCategory,
  CallObjection,
  TimingInsights,
  CorrelationsResult,
  BlockMeta,
  ScriptSectionOption,
  ScriptSection,
} from '../types';

export const salesCrmApi = {
  listLeads: (status?: string, niche?: string) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (niche) qs.set('niche', niche);
    const q = qs.toString();
    return api.get<Lead[]>(`/sales-crm/leads${q ? `?${q}` : ''}`);
  },

  getLead: (id: number) =>
    api.get<Lead & { calls: SaleCall[] }>(`/sales-crm/leads/${id}`),

  addLead: (data: Partial<Lead>) =>
    api.post<Lead>('/sales-crm/leads', data),

  updateLead: (id: number, data: Partial<Lead>) =>
    api.patch<Lead>(`/sales-crm/leads/${id}`, data),

  listCalls: (params?: { lead_id?: number; outcome?: string }) => {
    const qs = new URLSearchParams();
    if (params?.lead_id) qs.set('lead_id', String(params.lead_id));
    if (params?.outcome) qs.set('outcome', params.outcome);
    const q = qs.toString();
    return api.get<SaleCall[]>(`/sales-crm/calls${q ? `?${q}` : ''}`);
  },

  addCall: (data: {
    lead_id: number;
    length_seconds?: number;
    outcome?: string;
    emotions?: string[];
    notes?: string;
    // Wessen Nummer gewählt wurde — der Kommentar aus „Notizen" hängt an dieser Person (sonst firmenweit).
    person_key?: string;
    person_name?: string;
    caller_id?: string;
    call_time?: string; // client-local 'YYYY-MM-DD HH:MM:SS' for weekday/hour correlations
    // Wann wieder anrufen. UTC-ISO mit 'Z'. Der Server schreibt es nach domain_leads.callback_at
    // (Telefon-Stapel) und nach leads.follow_up_date — der Client fasst follow_up_date nicht mehr an.
    callback_at?: string;
    script_variant_id?: number;
    call_script_sections?: Record<string, { title: string; body: string }>;
    call_script_section_order?: string[];
    // Welche Absätze im Gespräch tatsächlich auf dem Schirm standen. Getrennt von den beiden
    // Feldern darüber: die halten den Schnappschuss des ganzen Skripts, also was zur Auswahl
    // gestanden HÄTTE. Das Erfolgs-Buch braucht, was benutzt WURDE.
    benutzte_absaetze?: string[];
    personalization_context?: Record<string, unknown>;
    // Sagt dem Server, ob gleich eine Aufnahme nachgereicht wird (uploadCallRecording) — sonst
    // postet er sofort ohne Audio nach #call-review, statt auf den Upload zu warten.
    has_recording?: boolean;
  }) => api.post<{ call: SaleCall; suggested_status: string | null; callback_at: string | null }>(
    '/sales-crm/calls', data),

  // Aufnahme + Transkription (siehe past-plans/ich-will-bei-jedem-logical-finch.md). Kein
  // api.post() moeglich (Multipart statt JSON) — gleiches Fetch-Muster wie
  // reelBuilderApi.uploadVideo.
  uploadCallRecording: async (callId: number, blob: Blob): Promise<{ call_id: number; status: string }> => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', blob, 'aufnahme.webm');
    const res = await fetch(`/api/sales-crm/calls/${callId}/recording`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Upload fehlgeschlagen (${res.status})`);
    }
    return res.json();
  },

  getCallRecordingStatus: (callId: number) =>
    api.get<{ call_id: number; status: string; error: string | null; transcribed_at: string | null }>(
      `/sales-crm/calls/${callId}/recording`),

  // Voller Transkript-Text, im Verlauf hinter "Transkript öffnen" (PR 5).
  getCallTranscript: (callId: number) =>
    api.get<{ call_id: number; transcript_text: string }>(`/sales-crm/calls/${callId}/transcript`),

  getTimingInsights: (niche?: string) =>
    api.get<TimingInsights>(`/sales-crm/timing-insights${niche ? `?niche=${encodeURIComponent(niche)}` : ''}`),

  getCorrelations: (niche?: string, minSample?: number) => {
    const qs = new URLSearchParams();
    if (niche) qs.set('niche', niche);
    if (minSample != null) qs.set('min_sample', String(minSample));
    const q = qs.toString();
    return api.get<CorrelationsResult>(`/sales-crm/correlations${q ? `?${q}` : ''}`);
  },

  getScript: () =>
    api.get<{ content: string; version: number }>('/sales-crm/script'),

  getScriptVersions: () =>
    api.get<ScriptVersion[]>('/sales-crm/script/versions'),

  getScriptVersion: (id: number) =>
    api.get<ScriptVersion>(`/sales-crm/script/versions/${id}`),

  getKpis: () =>
    api.get<SalesKpis>('/sales-crm/kpis'),

  getDueFollowups: () =>
    api.get<Lead[]>('/sales-crm/followups/due'),

  // ---------------------------------------------------------------------------
  // Variants
  // ---------------------------------------------------------------------------

  listVariants: (params?: { niche?: string; status?: string; bereich?: FassungsBereich }) => {
    const qs = new URLSearchParams();
    if (params?.niche) qs.set('niche', params.niche);
    if (params?.status) qs.set('status', params.status);
    if (params?.bereich) qs.set('bereich', params.bereich);
    const q = qs.toString();
    return api.get<ScriptVariant[]>(`/skript-bibliothek/variants${q ? `?${q}` : ''}`);
  },

  getVariant: (id: number) =>
    api.get<ScriptVariant>(`/skript-bibliothek/variants/${id}`),

  createVariant: (data: Partial<ScriptVariant>) =>
    api.post<ScriptVariant>('/skript-bibliothek/variants', data),

  updateVariant: (id: number, data: Partial<ScriptVariant>) =>
    api.patch<ScriptVariant>(`/skript-bibliothek/variants/${id}`, data),

  activateVariant: (id: number) =>
    api.post<{ ok: boolean }>(`/skript-bibliothek/variants/${id}/activate`, {}),

  archiveVariant: (id: number) =>
    api.post<{ ok: boolean }>(`/skript-bibliothek/variants/${id}/archive`, {}),

  evolveVariants: (variant_ids: number[], target_niche: string) =>
    api.post<ScriptVariant>('/sales-crm/variants/evolve', { variant_ids, target_niche }),

  seedVariant: (source_variant_id: number, target_niche: string, niche_context?: string) =>
    api.post<ScriptVariant>('/sales-crm/variants/seed', { source_variant_id, target_niche, niche_context }),

  getVariantLeaderboard: (niche?: string) => {
    const qs = niche ? `?niche=${encodeURIComponent(niche)}` : '';
    return api.get<VariantLeaderboardEntry[]>(`/sales-crm/variants/leaderboard${qs}`);
  },

  getEvolutionSuggestion: (niche?: string) => {
    const qs = niche ? `?niche=${encodeURIComponent(niche)}` : '';
    return api.get<EvolutionSuggestion>(`/sales-crm/variants/evolution-suggest${qs}`);
  },

  migrateGlobalScript: () =>
    api.post<ScriptVariant>('/skript-bibliothek/variants/migrate-global', {}),

  // ---------------------------------------------------------------------------
  // Git-Schicht: main (Champion) · persönlicher Fork · Block-Leaderboard · Promote
  // ---------------------------------------------------------------------------

  getMainScript: (niche = '_global', language = 'de') =>
    api.get<{ main: ScriptVariant | null; script: EffectiveScript | null }>(
      `/skript-bibliothek/variants/main?niche=${encodeURIComponent(niche)}&language=${language}`,
    ),

  // `leadId` (domain_leads.id): der Server löst daraus das Land auf (ADR 0214). Nicht-DACH ohne
  // englische Main → `main: null` + `hinweis` statt des deutschen Skripts.
  getMyFork: (niche = '_global', language = 'de', bereich: FassungsBereich = 'skript',
              leadId?: number | null) =>
    api.get<MyForkResponse>(
      `/skript-bibliothek/variants/my-fork?niche=${encodeURIComponent(niche)}&language=${language}`
      + `&bereich=${bereich}` + (leadId ? `&lead_id=${leadId}` : ''),
    ),

  // Zu jedem Absatz des EIGENEN Skripts die Fassungen, zwischen denen man wählen kann — dasselbe
  // Muster wie `getLibrarySectionOptions`, aber für den täglichen Weg statt für einen Regal-Eintrag.
  // Der eigene Text ist NICHT dabei (er ist das „Original", das die Karte ohnehin zeigt).
  getMyForkOptions: (niche = '_global', language = 'de', bereich: FassungsBereich = 'skript') =>
    api.get<{
      options: Record<string, ScriptSectionOption[]>;
      sub_options: Record<string, Record<string, ScriptSectionOption[]>>;
    }>(
      `/skript-bibliothek/variants/my-fork/options?niche=${encodeURIComponent(niche)}`
      + `&language=${language}&bereich=${bereich}`,
    ),

  getBlockLeaderboard: (niche = '_global', language = 'de',
                        bereich: FassungsBereich = 'skript') =>
    api.get<BlockLeaderboard>(
      `/sales-crm/variants/block-leaderboard?niche=${encodeURIComponent(niche)}`
      + `&language=${language}&bereich=${bereich}`,
    ),

  // Überschreibt einen Block im eigenen Fork; gibt Fork + neu zusammengesetztes Skript zurück.
  setForkBlock: (p: { niche?: string; language?: string; section_key: string; title: string;
                     body: string; bereich?: FassungsBereich }) =>
    api.patch<{ fork: ScriptVariant; script: EffectiveScript }>('/skript-bibliothek/variants/fork-block', {
      niche: '_global', language: 'de', ...p,
    }),

  // Ein umgebautes Skript als EIGENE Fassung übernehmen — gilt ab dem nächsten Call, nur für mich.
  // Ein Aufruf statt einer Kette von setForkBlock(): beim Umbau ändern sich mehrere Blöcke auf
  // einmal, und einzeln geschickt hinterliesse ein Abbruch ein halb umgebautes Skript.
  applyToMyFork: (p: {
    niche?: string;
    language?: string;
    sections_data: Record<string, ScriptSection>;
    section_order?: string[];
    bereich?: FassungsBereich;
  }) => api.post<{
    ok: boolean;
    fork: ScriptVariant;
    main: ScriptVariant | null;
    script: EffectiveScript;
    eigene_bloecke: number;
  }>('/skript-bibliothek/variants/fork-apply', { niche: '_global', language: 'de', ...p }),

  // Veröffentlicht einen Block sofort als eigenständigen, für alle sichtbaren Branch — kein 30er-Gate.
  shareBlock: (p: { niche?: string; language?: string; section_key: string; title: string;
                    body: string; name: string; bereich?: FassungsBereich }) =>
    api.post<{ ok: boolean; variant: ScriptVariant }>('/skript-bibliothek/variants/share-block', {
      niche: '_global', language: 'de', ...p,
    }),

  // Nimmt die Block-Überschreibung zurück → Block erbt wieder von Main.
  resetForkBlock: (p: { niche?: string; language?: string; section_key: string;
                       bereich?: FassungsBereich }) =>
    api.post<{ fork: ScriptVariant; script: EffectiveScript }>('/skript-bibliothek/variants/fork-block/reset', {
      niche: '_global', language: 'de', ...p,
    }),

  // Blendet einen Block im Fork aus/ein (persistent; wird im Call übersprungen).
  setForkHidden: (p: { niche?: string; language?: string; section_key: string; hidden: boolean;
                      bereich?: FassungsBereich }) =>
    api.post<{ fork: ScriptVariant; script: EffectiveScript }>('/skript-bibliothek/variants/fork-hidden', {
      niche: '_global', language: 'de', ...p,
    }),

  setMainVariant: (id: number) =>
    api.post<{ ok: boolean; main: ScriptVariant }>(`/skript-bibliothek/variants/${id}/set-main`, {}),

  // Merge-back (server-seitig hinterm 30er-Gate) · wirft 400 mit Grund, wenn noch nicht freigegeben.
  promoteBlock: (forkId: number, section_key: string) =>
    api.post<{ ok: boolean; main: ScriptVariant; section_key: string }>(
      `/skript-bibliothek/variants/${forkId}/promote-block`, { section_key },
    ),

  promoteAll: (forkId: number) =>
    api.post<{ ok: boolean; main: ScriptVariant }>(`/skript-bibliothek/variants/${forkId}/promote-all`, {}),

  // ---------------------------------------------------------------------------
  // Skript-Pool: ganze Skripte einstellen · ansehen · übernehmen
  // ---------------------------------------------------------------------------

  listLibraryScripts: (params?: { niche?: string; language?: string; includeArchived?: boolean;
                                  bereich?: FassungsBereich }) => {
    const qs = new URLSearchParams();
    if (params?.niche) qs.set('niche', params.niche);
    if (params?.language) qs.set('language', params.language);
    if (params?.includeArchived) qs.set('include_archived', 'true');
    if (params?.bereich) qs.set('bereich', params.bereich);
    const q = qs.toString();
    return api.get<LibraryScript[]>(`/skript-bibliothek/library${q ? `?${q}` : ''}`);
  },

  // `content`: der ABLAUF als Text — `## Überschrift` trennt die Blöcke, ohne Überschrift ein Block.
  // `objections`: die EINWÄNDE, gleiche Schreibweise, eigener Bereich. Wer das Feld mitschickt —
  // auch leer —, bekommt Blöcke MIT Bereichsvermerk; wer es weglässt, den früheren Weg.
  addLibraryScript: (data: {
    name: string;
    content?: string;
    objections?: string;
    sections_data?: Record<string, ScriptSection>;
    section_order?: string[];
    niche?: string;
    language?: string;
    notes?: string;
    bereich?: FassungsBereich;
  }) => api.post<LibraryScript>('/skript-bibliothek/library', data),

  updateLibraryScript: (id: number, data: Partial<LibraryScript> & { content?: string; objections?: string }) =>
    api.patch<LibraryScript>(`/skript-bibliothek/library/${id}`, data),

  removeLibraryScript: (id: number) =>
    api.delete<{ ok: boolean }>(`/skript-bibliothek/library/${id}`),

  // Übernimmt das Skript 1:1 in den eigenen Fork → ab dem nächsten Call aktiv.
  // niche/language = Fork-Kontext (vor einem Call die Nische des Leads); ohne Angabe die des Skripts.
  adoptLibraryScript: (id: number, ctx?: { niche?: string; language?: string }) =>
    api.post<{ ok: boolean; fork: ScriptVariant; main: ScriptVariant | null; script: EffectiveScript }>(
      `/skript-bibliothek/library/${id}/adopt`, ctx ?? {},
    ),

  // `released`: wie viele Arbeits-Skripte dem neuen Team-Stand wieder folgen — die reinen Kopien
  // in den Forks werden dabei losgelassen, eigene Änderungen bleiben (release_copied_overrides).
  setLibraryScriptAsMain: (id: number) =>
    api.post<{ ok: boolean; main: ScriptVariant; released: number }>(`/skript-bibliothek/library/${id}/set-main`, {}),

  // Umbauen: die Fassungen, zwischen denen man wählen kann — auf zwei Ebenen. `options` je
  // `##`-Absatz, `sub_options` je `###`-Unterüberschrift darin (nach genormtem Titel). Der eigene
  // Text des Skripts ist NICHT dabei (er ist das „Original", das die Oberfläche ohnehin zeigt).
  getLibrarySectionOptions: (id: number) =>
    api.get<{
      options: Record<string, ScriptSectionOption[]>;
      sub_options: Record<string, Record<string, ScriptSectionOption[]>>;
    }>(`/skript-bibliothek/library/${id}/options`),

  addScriptSectionOption: (data: {
    section_key: string;
    title: string;
    body: string;
    label?: string;
    niche?: string;
    language?: string;
    bereich?: FassungsBereich;
    // Mit `sub_key` gilt die Fassung nur für diese eine Unterüberschrift, ohne für den ganzen Absatz.
    sub_key?: string;
  }) => api.post<ScriptSectionOption>('/skript-bibliothek/script-options', data),

  removeScriptSectionOption: (optionId: number, bereich: FassungsBereich = 'skript') =>
    api.delete<{ ok: boolean }>(`/skript-bibliothek/script-options/${optionId}?bereich=${bereich}`),

  resetMyFork: (niche = '_global', language = 'de', bereich: FassungsBereich = 'skript') =>
    api.post<{ ok: boolean; fork: ScriptVariant; main: ScriptVariant | null; script: EffectiveScript }>(
      '/skript-bibliothek/library/reset-fork', { niche, language, bereich },
    ),

  // ---------------------------------------------------------------------------
  // Bibliothek (2026-08-07): Block-Metadaten und Main-Blöcke schreibt nur admin/HoS — siehe
  // api/middleware/setter_acl.py. Gelesen werden die Metadaten über getMyFork mit.
  // ---------------------------------------------------------------------------

  /**
   * Die Zielgruppen der BIBLIOTHEK dieses Kanals.
   *
   * Nicht `/sales-crm/niches`: der zaehlt zusaetzlich Leads und Accounts und beantwortet damit
   * „wen rufen wir an". Hier geht es um „wofuer haben wir Texte". Fuer Mail und LinkedIn kommt
   * heute eine leere Liste zurueck, und dann zeigt die Ansicht keinen Filter — statt eines
   * Feldes, das tippbar ist und nichts findet.
   */
  getBibliotheksNischen: (language = 'de', bereich: FassungsBereich = 'skript') =>
    api.get<string[]>(`/skript-bibliothek/zielgruppen?lang=${encodeURIComponent(language)}` +
                      `&bereich=${bereich}`),

  setBlockMeta: (niche: string, language: string, section_key: string, meta: Partial<BlockMeta>,
                 bereich: FassungsBereich = 'skript') =>
    api.patch<{ ok: boolean; main: ScriptVariant }>('/skript-bibliothek/variants/block-meta', {
      niche, language, section_key, bereich, ...meta,
    }),

  addMainBlock: (data: {
    niche: string; language: string; section_key: string; title: string; body?: string;
    group?: string; kind?: string; draft?: boolean; objections?: string[]; position?: number;
    bereich?: FassungsBereich;
  }) => api.post<{ ok: boolean; main: ScriptVariant }>('/skript-bibliothek/variants/main-block', data),

  renameMainBlock: (niche: string, language: string, section_key: string,
                    data: { title?: string; body?: string; bereich?: FassungsBereich }) =>
    api.patch<{ ok: boolean; main: ScriptVariant }>('/skript-bibliothek/variants/main-block', {
      niche, language, section_key, ...data,
    }),

  reorderMainBlocks: (niche: string, language: string, section_order: string[],
                      bereich: FassungsBereich = 'skript') =>
    api.post<{ ok: boolean; main: ScriptVariant }>('/skript-bibliothek/variants/main-block/reorder', {
      niche, language, section_order, bereich,
    }),

  deleteMainBlock: (niche: string, language: string, section_key: string,
                    bereich: FassungsBereich = 'skript') =>
    api.post<{ ok: boolean; main: ScriptVariant }>('/skript-bibliothek/variants/main-block/delete', {
      niche, language, section_key, bereich,
    }),

  // ---------------------------------------------------------------------------
  // Section tags & call scripts
  // ---------------------------------------------------------------------------

  saveSectionTags: (callId: number, strongest: string | null, weakest: string | null) =>
    api.post<{ ok: boolean }>(`/sales-crm/calls/${callId}/section-tags`, { strongest, weakest }),

  getSectionTags: (callId: number) =>
    api.get<SectionTags>(`/sales-crm/calls/${callId}/section-tags`),

  getCallScript: (callId: number) =>
    api.get<CallScript>(`/sales-crm/calls/${callId}/script`),

  // ---------------------------------------------------------------------------
  // Niches
  // ---------------------------------------------------------------------------

  getNiches: (lang?: string) =>
    api.get<string[]>(`/sales-crm/niches${lang ? `?lang=${lang}` : ''}`),

  // ---------------------------------------------------------------------------
  // Objection Categories
  // ---------------------------------------------------------------------------

  listObjectionCategories: (niche?: string): Promise<ObjectionCategory[]> => {
    const qs = niche ? `?niche=${encodeURIComponent(niche)}` : '';
    return api.get<ObjectionCategory[]>(`/sales-crm/objection-categories${qs}`);
  },

  createObjectionCategory: (data: Partial<ObjectionCategory>): Promise<ObjectionCategory> =>
    api.post<ObjectionCategory>('/sales-crm/objection-categories', data),

  updateObjectionCategory: (id: number, data: Partial<ObjectionCategory>): Promise<ObjectionCategory> =>
    api.patch<ObjectionCategory>(`/sales-crm/objection-categories/${id}`, data),

  seedObjectionTaxonomy: (niche?: string, limit?: number): Promise<ObjectionCategory[]> =>
    api.post<ObjectionCategory[]>('/sales-crm/objection-categories/seed', { niche, limit }),

  getCallObjections: (callId: number): Promise<CallObjection[]> =>
    api.get<CallObjection[]>(`/sales-crm/calls/${callId}/objections`),

  getFlaggedObjections: (): Promise<CallObjection[]> =>
    api.get<CallObjection[]>('/sales-crm/objections/flagged'),

  resolveObjection: (id: number, categoryId: number): Promise<void> =>
    api.post<void>(`/sales-crm/objections/${id}/resolve`, { category_id: categoryId }),

  // ---------------------------------------------------------------------------
  // Sales Insights
  // ---------------------------------------------------------------------------

  getInsights: (niche?: string, force?: boolean): Promise<any> => {
    const qs = new URLSearchParams();
    if (niche) qs.set('niche', niche);
    if (force) qs.set('force', 'true');
    const q = qs.toString();
    return api.get<any>(`/sales-crm/insights${q ? `?${q}` : ''}`);
  },

  getObjectionTrends: (niche?: string): Promise<any> => {
    const qs = niche ? `?niche=${encodeURIComponent(niche)}` : '';
    return api.get<any>(`/sales-crm/insights/objection-trends${qs}`);
  },

  syncSalesBridge: (niche: string, strategyId: number): Promise<{created_experiment_ids: number[], count: number}> =>
    api.post<{created_experiment_ids: number[], count: number}>('/sales-crm/sales-bridge/sync', { niche, strategy_id: strategyId }),

  // ---------------------------------------------------------------------------
  // Job Discovery
  // ---------------------------------------------------------------------------

  leadsFromJobs: (data: {
    search_terms: string[];
    location: string;
    max_results: number;
    auto_score: boolean;
  }) => api.post<{ job_id: string; status: string; message: string }>('/sales-crm/leads/from-jobs', data),

  scoreLead: (leadId: number) =>
    api.post<{ scores: import('../types').FitScore[]; best: import('../types').FitScore | null }>(`/sales-crm/leads/${leadId}/score`, {}),

  leadToOutreach: (leadId: number) =>
    api.post<{ target_id: number; mapped_fields: Record<string, unknown> }>(`/sales-crm/leads/${leadId}/to-outreach`, {}),

  getLeadJobPostings: (leadId: number) =>
    api.get<import('../types').JobPosting[]>(`/sales-crm/leads/${leadId}/job-postings`),
};
